import { InjectionToken, Provider } from '@angular/core';
import { ApolloClient, ApolloLink, InMemoryCache, split } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';

import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import extractFiles from 'extract-files/public/extractFiles.js';
import { environment } from '../environments/environment';

const uri = `${environment.coreApiUrl}/graphql`;
const wsUri = `${environment.coreApiUrlWs}/graphql`;

export const WS_LINK = new InjectionToken<WebSocketLink>('WS_LINK');

function getToken(): string {
  const stringToken = sessionStorage.getItem('dux.token');
  if (stringToken == null) return null;
  return stringToken.substring(1, stringToken.length - 1);
}

function getTenantKey(): string {
  const tenantJson = sessionStorage.getItem('dux.user.tenant');
  if (tenantJson == null) return null;
  const tenant = JSON.parse(tenantJson);
  return tenant['key'];
}

function createWsLink(): WebSocketLink {
  return new WebSocketLink({
    uri: wsUri,
    options: {
      lazy: true,
      reconnect: true,
      connectionParams: () => ({
        authorization: `Bearer ${getToken()}`,
        'X-Tenant-Key': getTenantKey(),
      }),
    },
  });
}

export function createApollo(httpLink: HttpLink, ws: WebSocketLink): ApolloClient<any> {
  const http = httpLink.create({
    uri: uri,
    extractFiles: extractFiles,
  });

  const authLink = setContext((_, { headers }) => {
    const token = getToken();
    const tenantKey = getTenantKey();
    const newHeaders = { ...headers };
    if (token) newHeaders['Authorization'] = `Bearer ${token}`;
    if (tenantKey) newHeaders['X-Tenant-Key'] = tenantKey;
    return { headers: newHeaders };
  });

  const link = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    authLink.concat(ws),
    authLink.concat(http),
  );

  return new ApolloClient<any>({
    link: ApolloLink.from([link]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: { fetchPolicy: 'no-cache', errorPolicy: 'none' },
      query: { fetchPolicy: 'no-cache', errorPolicy: 'none' },
      mutate: { fetchPolicy: 'no-cache', errorPolicy: 'none' },
    },
  });
}

export const graphqlProviders: Provider[] = [
  Apollo,
  {
    provide: WS_LINK,
    useFactory: createWsLink,
  },
  {
    provide: APOLLO_OPTIONS,
    useFactory: createApollo,
    deps: [HttpLink, WS_LINK],
  },
];
